import type { PresentationSlide } from '../../../generated/graphql-viewer';
import type { CSSProperties, HTMLAttributes } from 'react';

import { classNames } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { useGetWebcastViewerSlidesQuery } from '../../../generated/graphql-viewer';
import { useSelectedLanguage } from '../../providers/selected-language-provider';
import { useViewerAccess } from '../../providers/viewer-access-provider';

import styles from './slide-preview.module.css';

export type SlideCuepoint = {
  webcastId: string;
  groupId: string;
  index: number;
  language?: string;
};

type Props = {
  slideCuepoint?: SlideCuepoint;
} & HTMLAttributes<HTMLDivElement>;

export function SlidePreview({ slideCuepoint, className, ...props }: Props) {
  const { webcastId = '' } = useParams();
  const { selectedLanguage, primaryLanguage, isCustomLanguage } = useSelectedLanguage();
  const { email, accessCode, protectionToken } = useViewerAccess();

  const getWebcastViewerSlidesResponse = useGetWebcastViewerSlidesQuery({
    variables: { webcastId, accessCode, email, protectionToken },
    skip: !webcastId,
    // Backend clears the current slide when switching the event state,
    // so we need to refetch the data when this component remounts
    fetchPolicy: 'no-cache',
  });

  const webcast = getWebcastViewerSlidesResponse.data?.webcast;
  const language = isCustomLanguage(selectedLanguage?.code) ? primaryLanguage?.code : selectedLanguage?.code;

  const [slide, setSlide] = useState<Pick<PresentationSlide, 'index' | 'downloadUrl'> | null>(null);

  useEffect(() => {
    const presentation = webcast?.presentations.find((presentation) => presentation.language === language);
    const slides = presentation?.slides || [];
    const currentSlide = slides.find((slide) => slide?.index === webcast?.currentSlide?.index);
    const slideFromCuepoint = slides.find((slide) => slide?.index === slideCuepoint?.index);

    setSlide(slideFromCuepoint || currentSlide || null);
  }, [language, slideCuepoint?.index, webcast]);

  const [aspectRatio, setAspectRatio] = useState(1);

  useEffect(() => {
    if (slide?.downloadUrl) {
      const img = new Image();
      img.onload = () => setAspectRatio(img.width / img.height);
      img.src = slide.downloadUrl;
    }
  }, [slide?.downloadUrl]);

  if (!slide) return null;

  return (
    <img
      className={classNames(styles.slidePreview, className)}
      src={slide.downloadUrl}
      style={{ '--aspectRatio': aspectRatio } as CSSProperties}
      data-testid={`slide-${slide.index}`}
      alt=""
      {...props}
    />
  );
}
